import React, { useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react';
import { Logo } from '../components/ui/Logo';

const GREEN = '#4ADE80';
const GREEN_LIGHT = '#F0FDF4';
const GREEN_LIGHTER = '#ECFDF5';
const GREEN_10 = 'rgba(74, 222, 128, 0.1)';
const GREEN_20 = 'rgba(74, 222, 128, 0.2)';
const DARK = '#1F2937';
const GRAY = '#6B7280';
const LIGHT_GRAY = '#F9FAFB';
const BORDER_GRAY = '#E5E7EB';

// ─── Navigation ─────────────────────────────────────────────────────────
function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  return (
    <nav style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
      background: scrolled ? 'rgba(255,255,255,0.95)' : '#fff',
      boxShadow: scrolled ? '0 2px 20px rgba(0,0,0,0.06)' : 'none',
      transition: 'all 0.3s ease',
      backdropFilter: scrolled ? 'blur(8px)' : 'none',
    }}>
      <div style={{ maxWidth: 1280, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px' }}>
        <a href="/homepage-final" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
          <Logo size="md" />
        </a>
        <div className="hf-nav-links" style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
          <a href="#features" style={{ fontSize: 15, fontWeight: 500, color: DARK, textDecoration: 'none' }}>Features</a>
          <a href="#how-it-works" style={{ fontSize: 15, fontWeight: 500, color: DARK, textDecoration: 'none' }}>How It Works</a>
          <a href="#pricing" style={{ fontSize: 15, fontWeight: 500, color: DARK, textDecoration: 'none' }}>Pricing</a>
          <a href="/auth?tab=register" style={{
            fontSize: 15, fontWeight: 600, color: '#fff', textDecoration: 'none',
            background: GREEN, padding: '10px 24px', borderRadius: 8,
            transition: 'all 0.2s ease',
          }}>Get Started</a>
        </div>
        <button className="hf-hamburger" onClick={() => setMenuOpen(!menuOpen)} style={{
          display: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 8,
        }}>
          {menuOpen ? <X width={24} height={24} stroke={DARK} /> : <Menu width={24} height={24} stroke={DARK} />}
        </button>
      </div>
      {menuOpen && (
        <div className="hf-mobile-menu" style={{
          position: 'fixed', top: 64, left: 0, right: 0, bottom: 0,
          background: '#fff', padding: '40px 24px', display: 'flex', flexDirection: 'column', gap: 24, zIndex: 999,
        }}>
          <a href="#features" onClick={() => setMenuOpen(false)} style={{ fontSize: 20, fontWeight: 500, color: DARK, textDecoration: 'none' }}>Features</a>
          <a href="#how-it-works" onClick={() => setMenuOpen(false)} style={{ fontSize: 20, fontWeight: 500, color: DARK, textDecoration: 'none' }}>How It Works</a>
          <a href="#pricing" onClick={() => setMenuOpen(false)} style={{ fontSize: 20, fontWeight: 500, color: DARK, textDecoration: 'none' }}>Pricing</a>
          <a href="/auth?tab=register" style={{
            fontSize: 18, fontWeight: 600, color: '#fff', textDecoration: 'none', textAlign: 'center',
            background: GREEN, padding: '14px 24px', borderRadius: 8,
          }}>Get Started Free</a>
        </div>
      )}
    </nav>
  );
}

// ─── Hero Section ───────────────────────────────────────────────────────
function Hero() {
  return (
    <section style={{ paddingTop: 120, paddingBottom: 80, background: `linear-gradient(180deg, #fff 0%, ${GREEN_LIGHT} 100%)`, position: 'relative', overflow: 'hidden' }}>
      {/* Subtle animated circles */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
        <div className="hf-float-circle" style={{ position: 'absolute', width: 300, height: 300, borderRadius: '50%', background: GREEN_10, top: -50, right: -80 }} />
        <div className="hf-float-circle hf-float-delay" style={{ position: 'absolute', width: 200, height: 200, borderRadius: '50%', background: GREEN_10, bottom: 40, left: -60 }} />
        <div className="hf-float-circle hf-float-delay2" style={{ position: 'absolute', width: 150, height: 150, borderRadius: '50%', background: GREEN_20, top: '40%', right: '15%' }} />
      </div>
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 24px', textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <h1 style={{ fontSize: 'clamp(36px, 5vw, 64px)', fontWeight: 800, lineHeight: 1.1, color: DARK, margin: '0 0 24px' }}>
          Create Stunning Images with<br />
          <span style={{ color: GREEN }}>Visual AI Workflows</span>
        </h1>
        <p style={{ fontSize: 'clamp(16px, 2vw, 20px)', color: GRAY, maxWidth: 640, margin: '0 auto 40px', lineHeight: 1.6 }}>
          Drag, connect, and generate. Build powerful image generation workflows on an intuitive visual canvas powered by AI.
        </p>
        <div className="hf-hero-ctas" style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
          <a href="/auth?tab=register" className="hf-btn-primary" style={{
            background: GREEN, color: '#fff', fontSize: 17, fontWeight: 600,
            padding: '16px 32px', borderRadius: 10, textDecoration: 'none',
            boxShadow: `0 4px 20px ${GREEN_20}`, transition: 'all 0.2s ease',
          }}>Start Creating Free</a>
          <a href="#canvas-experience" className="hf-btn-secondary" style={{
            background: '#fff', color: DARK, fontSize: 17, fontWeight: 600,
            padding: '16px 32px', borderRadius: 10, textDecoration: 'none',
            border: `2px solid ${BORDER_GRAY}`, transition: 'all 0.2s ease',
          }}>See How It Works</a>
        </div>

        {/* Canvas Mockup */}
        <div style={{ marginTop: 60, background: GREEN_LIGHTER, borderRadius: 16, padding: 'clamp(24px, 4vw, 48px)', maxWidth: 900, margin: '60px auto 0', boxShadow: '0 8px 40px rgba(0,0,0,0.06)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24 }}>
            <span style={{ background: GREEN, color: '#fff', fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 20 }}>✨ Ready</span>
            <span style={{ fontSize: 13, color: GRAY }}>Visual Workflow Canvas</span>
          </div>
          <div className="hf-canvas-nodes" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0, flexWrap: 'wrap' }}>
            <CanvasNode label="Text Prompt" icon="📝" subtitle="A serene mountain..." />
            <Arrow />
            <CanvasNode label="References" icon="🖼️" subtitle="4 images attached" />
            <Arrow />
            <CanvasNode label="Generate" icon="⚡" subtitle="Gemini AI" highlight />
            <Arrow />
            <CanvasNode label="Output" icon="✨" subtitle="1024×1024" />
          </div>
        </div>

        {/* Stats bar */}
        <div className="hf-stats-bar" style={{ display: 'flex', justifyContent: 'center', gap: 'clamp(24px, 4vw, 60px)', marginTop: 40, flexWrap: 'wrap' }}>
          <StatItem value="2,500+" label="Images Generated" />
          <StatItem value="500+" label="Active Creators" />
          <StatItem value="14" label="References per Generation" />
        </div>
      </div>
    </section>
  );
}

function CanvasNode({ label, icon, subtitle, highlight }: { label: string; icon: string; subtitle: string; highlight?: boolean }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: '16px 20px', minWidth: 140, textAlign: 'center',
      border: highlight ? `2px solid ${GREEN}` : `1px solid ${BORDER_GRAY}`,
      boxShadow: highlight ? `0 0 12px ${GREEN_20}` : '0 2px 8px rgba(0,0,0,0.04)',
    }}>
      <div style={{ fontSize: 24, marginBottom: 6 }}>{icon}</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: DARK }}>{label}</div>
      <div style={{ fontSize: 12, color: GRAY, marginTop: 2 }}>{subtitle}</div>
    </div>
  );
}

function Arrow() {
  return (
    <div className="hf-arrow" style={{ padding: '0 8px', display: 'flex', alignItems: 'center' }}>
      <svg width="32" height="12" fill="none"><path d="M0 6h28m0 0l-5-5m5 5l-5 5" stroke={GREEN} strokeWidth="2" /></svg>
    </div>
  );
}

function StatItem({ value, label }: { value: string; label: string }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 'clamp(20px, 3vw, 28px)', fontWeight: 700, color: GREEN }}>{value}</div>
      <div style={{ fontSize: 14, color: GRAY }}>{label}</div>
    </div>
  );
}

// ─── Features Section ───────────────────────────────────────────────────
const features = [
  { icon: '🎨', title: 'Visual Canvas', desc: 'Drag-and-drop node-based workflow editor. Place prompts, images, and settings on an infinite canvas and connect them visually.' },
  { icon: '⚡', title: 'AI-Powered Generation', desc: 'Powered by Google Gemini. Generate high-quality images from text prompts with advanced AI understanding of your creative intent.' },
  { icon: '🖼️', title: 'Reference Images', desc: 'Use up to 14 reference images for precise control. Guide AI generation with visual examples for style, composition, and subject.' },
  { icon: '🔄', title: 'Real-time Sync', desc: 'Multi-browser canvas collaboration. Work on the same canvas across devices with instant synchronization of all changes.' },
  { icon: '📁', title: 'Gallery & History', desc: 'Organize and manage all your generated images in one place. Browse, search, and curate your AI-created visual library.' },
  { icon: '⚙️', title: 'Style Presets', desc: 'Save and reuse size, style, and generation configurations. Create consistent outputs by storing your preferred settings.' },
];

function Features() {
  return (
    <section id="features" style={{ padding: 'clamp(60px, 8vw, 100px) 24px', background: '#fff' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', textAlign: 'center' }}>
        <h2 style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 700, color: DARK, marginBottom: 12 }}>
          Everything You Need to <span style={{ color: GREEN }}>Create</span>
        </h2>
        <p style={{ fontSize: 18, color: GRAY, marginBottom: 60, maxWidth: 520, margin: '0 auto 60px' }}>
          A complete toolkit for AI-powered image generation workflows
        </p>
        <div className="hf-features-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
          {features.map((f, i) => (
            <div key={i} className="hf-feature-card" style={{
              background: LIGHT_GRAY, border: `1px solid ${BORDER_GRAY}`, borderRadius: 12,
              padding: 28, textAlign: 'left', transition: 'all 0.2s ease',
            }}>
              <div style={{
                width: 48, height: 48, borderRadius: 12, background: GREEN_10,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 22, marginBottom: 16,
              }}>{f.icon}</div>
              <h3 style={{ fontSize: 18, fontWeight: 600, color: DARK, marginBottom: 8 }}>{f.title}</h3>
              <p style={{ fontSize: 14, color: GRAY, lineHeight: 1.6, margin: 0 }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Canvas Experience Section ──────────────────────────────────────────
function CanvasExperience() {
  return (
    <section id="canvas-experience" style={{ padding: 'clamp(60px, 8vw, 100px) 24px', background: `linear-gradient(180deg, ${GREEN_LIGHT} 0%, #fff 100%)` }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', textAlign: 'center' }}>
        <h2 style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 700, color: DARK, marginBottom: 12 }}>
          The <span style={{ color: GREEN }}>Canvas</span> Experience
        </h2>
        <p style={{ fontSize: 18, color: GRAY, marginBottom: 48, maxWidth: 520, margin: '0 auto 48px' }}>
          Build complex image generation workflows visually
        </p>
        <div style={{ background: '#fff', borderRadius: 20, padding: 'clamp(24px, 4vw, 48px)', boxShadow: '0 8px 40px rgba(0,0,0,0.06)', border: `1px solid ${GREEN_20}`, position: 'relative' }}>
          {/* Annotations */}
          <div className="hf-canvas-annotations" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
            <Annotation text="Drag nodes onto canvas" />
            <Annotation text="Connect visually" />
            <Annotation text="Generate with one click" />
          </div>
          {/* Full workflow */}
          <div className="hf-canvas-full" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0, flexWrap: 'wrap', padding: '24px 0' }}>
            <CanvasNode label="Prompt" icon="📝" subtitle="Describe your image" />
            <Arrow />
            <CanvasNode label="References" icon="🖼️" subtitle="Style guide images" />
            <Arrow />
            <CanvasNode label="Style Preset" icon="⚙️" subtitle="1024×1024, Vivid" />
            <Arrow />
            <CanvasNode label="Generate" icon="⚡" subtitle="Gemini AI" highlight />
            <Arrow />
            <CanvasNode label="Output" icon="✨" subtitle="Ready to download" />
          </div>
          {/* Connection lines decoration */}
          <div style={{ height: 4, background: `linear-gradient(90deg, ${GREEN_LIGHT}, ${GREEN}, ${GREEN_LIGHT})`, borderRadius: 2, margin: '24px auto 0', maxWidth: 600 }} />
        </div>
      </div>
    </section>
  );
}

function Annotation({ text }: { text: string }) {
  return (
    <span style={{ fontSize: 13, color: GREEN, fontWeight: 600, background: GREEN_10, padding: '6px 14px', borderRadius: 20 }}>
      {text}
    </span>
  );
}

// ─── How It Works Section ───────────────────────────────────────────────
const steps = [
  { num: '1', title: 'Create Nodes', desc: 'Add prompt nodes for text descriptions, upload reference images, and configure preset nodes for size and style settings.' },
  { num: '2', title: 'Connect Your Workflow', desc: 'Draw connections between nodes to build your generation pipeline. Link references and prompts to a generate node.' },
  { num: '3', title: 'Generate with One Click', desc: 'Hit generate and watch as Google Gemini creates AI images informed by all your connected prompts and references.' },
];

function HowItWorks() {
  return (
    <section id="how-it-works" style={{ padding: 'clamp(60px, 8vw, 100px) 24px', background: '#fff' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', textAlign: 'center' }}>
        <h2 style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 700, color: DARK, marginBottom: 12 }}>
          How It <span style={{ color: GREEN }}>Works</span>
        </h2>
        <p style={{ fontSize: 18, color: GRAY, marginBottom: 60, maxWidth: 480, margin: '0 auto 60px' }}>
          Three simple steps to generate AI images
        </p>
        <div className="hf-steps-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 32, position: 'relative' }}>
          {steps.map((s, i) => (
            <div key={i} style={{ position: 'relative', textAlign: 'center' }}>
              <div style={{
                width: 48, height: 48, borderRadius: '50%', background: GREEN, color: '#fff',
                fontSize: 20, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 20px',
              }}>{s.num}</div>
              <h3 style={{ fontSize: 18, fontWeight: 600, color: DARK, marginBottom: 10 }}>{s.title}</h3>
              <p style={{ fontSize: 14, color: GRAY, lineHeight: 1.6, margin: 0 }}>{s.desc}</p>
              {i < 2 && <div className="hf-step-arrow" style={{ position: 'absolute', top: 24, right: -16, color: GREEN, fontSize: 20 }}>→</div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Use Cases Section ──────────────────────────────────────────────────
const useCases = [
  { icon: '🎨', title: 'Designers', desc: 'Create mood boards and iterate on visual concepts rapidly. Use reference images to maintain style consistency across projects.' },
  { icon: '📢', title: 'Marketers', desc: 'Generate campaign visuals at scale. Produce on-brand imagery for social media, ads, and content with repeatable workflows.' },
  { icon: '✍️', title: 'Content Creators', desc: 'Produce unique imagery for any platform. Build visual libraries of AI-generated content tailored to your audience and style.' },
];

function UseCases() {
  return (
    <section style={{ padding: 'clamp(60px, 8vw, 100px) 24px', background: GREEN_LIGHT }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', textAlign: 'center' }}>
        <h2 style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 700, color: DARK, marginBottom: 12 }}>
          Built for <span style={{ color: GREEN }}>Creatives</span>
        </h2>
        <p style={{ fontSize: 18, color: GRAY, marginBottom: 48, maxWidth: 540, margin: '0 auto 48px' }}>
          See how teams use Cascady to transform their creative workflow
        </p>
        <div className="hf-usecases-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
          {useCases.map((u, i) => (
            <div key={i} className="hf-feature-card" style={{
              background: '#fff', border: `1px solid ${BORDER_GRAY}`, borderRadius: 12,
              padding: 28, textAlign: 'left', transition: 'all 0.2s ease',
            }}>
              <div style={{
                width: 48, height: 48, borderRadius: '50%', background: GREEN_10,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 22, marginBottom: 16,
              }}>{u.icon}</div>
              <h3 style={{ fontSize: 18, fontWeight: 600, color: DARK, marginBottom: 8 }}>{u.title}</h3>
              <p style={{ fontSize: 14, color: GRAY, lineHeight: 1.6, margin: 0 }}>{u.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Testimonials Section ───────────────────────────────────────────────
const testimonials = [
  { quote: 'Cascady completely changed how I approach AI art. The canvas interface makes complex workflows feel natural.', name: 'Sarah Chen', role: 'Digital Artist' },
  { quote: 'Being able to use 14 reference images gives me control I never had before. The results are incredible.', name: 'Marcus Rivera', role: 'Creative Director' },
  { quote: 'Our team collaborates on canvases daily. It\'s become essential to our creative pipeline.', name: 'Aisha Patel', role: 'Studio Lead' },
];

function Testimonials() {
  return (
    <section style={{ padding: 'clamp(60px, 8vw, 100px) 24px', background: '#fff' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', textAlign: 'center' }}>
        <h2 style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 700, color: DARK, marginBottom: 12 }}>
          Loved by <span style={{ color: GREEN }}>Creators</span>
        </h2>
        <p style={{ fontSize: 18, color: GRAY, marginBottom: 48, maxWidth: 420, margin: '0 auto 48px' }}>
          See what our community is saying
        </p>
        <div className="hf-testimonials-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
          {testimonials.map((t, i) => (
            <div key={i} className="hf-feature-card" style={{
              background: '#fff', border: `1px solid ${BORDER_GRAY}`, borderRadius: 12,
              padding: 28, textAlign: 'left', borderTop: `3px solid ${GREEN}`,
              boxShadow: '0 4px 16px rgba(0,0,0,0.04)', transition: 'all 0.2s ease',
            }}>
              <div style={{ fontSize: 32, color: GREEN, marginBottom: 12, lineHeight: 1 }}>"</div>
              <p style={{ fontSize: 15, color: DARK, lineHeight: 1.6, marginBottom: 20, fontStyle: 'italic' }}>{t.quote}</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: '50%', background: GREEN_10,
                  border: `2px solid ${GREEN}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 16, fontWeight: 600, color: GREEN,
                }}>{t.name[0]}</div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: DARK }}>{t.name}</div>
                  <div style={{ fontSize: 13, color: GRAY }}>{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Pricing Preview Section ────────────────────────────────────────────
function PricingPreview() {
  return (
    <section id="pricing" style={{ padding: 'clamp(60px, 8vw, 80px) 24px', background: GREEN_LIGHT }}>
      <div style={{ maxWidth: 700, margin: '0 auto', textAlign: 'center' }}>
        <h2 style={{ fontSize: 'clamp(28px, 4vw, 36px)', fontWeight: 700, color: DARK, marginBottom: 16 }}>
          Simple, <span style={{ color: GREEN }}>Transparent</span> Pricing
        </h2>
        <p style={{ fontSize: 17, color: GRAY, lineHeight: 1.6, marginBottom: 32 }}>
          Start free with generous limits. Upgrade as your creative needs grow. No hidden fees, cancel anytime.
        </p>
        <a href="/pricing" className="hf-btn-primary" style={{
          display: 'inline-block', background: GREEN, color: '#fff', fontSize: 16, fontWeight: 600,
          padding: '14px 32px', borderRadius: 10, textDecoration: 'none',
          boxShadow: `0 4px 16px ${GREEN_20}`, transition: 'all 0.2s ease',
        }}>View Pricing Plans</a>
      </div>
    </section>
  );
}

// ─── Final CTA Section ──────────────────────────────────────────────────
function FinalCTA() {
  return (
    <section style={{ padding: 'clamp(60px, 8vw, 100px) 24px', background: '#fff', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        <div style={{ position: 'absolute', width: 200, height: 200, borderRadius: '50%', background: GREEN_10, bottom: -60, right: -40 }} />
        <div style={{ position: 'absolute', width: 120, height: 120, borderRadius: '50%', background: GREEN_10, top: 20, left: -30 }} />
      </div>
      <div style={{ maxWidth: 600, margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 700, color: DARK, marginBottom: 16 }}>
          Ready to <span style={{ color: GREEN }}>Create</span>?
        </h2>
        <p style={{ fontSize: 18, color: GRAY, marginBottom: 36 }}>
          Join thousands of creators building with visual AI workflows.
        </p>
        <a href="/auth?tab=register" className="hf-btn-primary" style={{
          display: 'inline-block', background: GREEN, color: '#fff', fontSize: 18, fontWeight: 600,
          padding: '18px 40px', borderRadius: 12, textDecoration: 'none',
          boxShadow: `0 6px 24px ${GREEN_20}`, transition: 'all 0.2s ease',
        }}>Get Started Free</a>
      </div>
    </section>
  );
}

// ─── Footer ─────────────────────────────────────────────────────────────
const footerColsData: { title: string; links: { label: string; href: string }[] }[] = [
  { title: 'PRODUCT', links: [
    { label: 'Features', href: '/' },
    { label: 'Pricing', href: '/pricing' },
    { label: 'Blog', href: '/blog' },
  ]},
  { title: 'SUPPORT', links: [
    { label: 'Help Center', href: '/help' },
    { label: 'Contact', href: '/contact' },
  ]},
  { title: 'LEGAL', links: [
    { label: 'Privacy Policy', href: '/privacy' },
    { label: 'Terms of Service', href: '/terms' },
    { label: 'Refund Policy', href: '/refund-policy' },
    { label: 'Cookie Policy', href: '/cookie-policy' },
  ]},
];

function Footer() {
  return (
    <footer style={{ background: '#111827', padding: 'clamp(48px, 6vw, 80px) 24px 32px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div className="hf-footer-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 32, marginBottom: 48 }}>
          {footerColsData.map((col, i) => (
            <div key={i}>
              <h4 style={{ fontSize: 13, fontWeight: 700, color: '#fff', letterSpacing: 1, marginBottom: 16, textTransform: 'uppercase' }}>{col.title}</h4>
              {col.links.map((link, j) => (
                <a key={j} href={link.href} className="hf-footer-link" style={{ display: 'block', fontSize: 14, color: '#9CA3AF', textDecoration: 'none', marginBottom: 10, transition: 'color 0.2s' }}>{link.label}</a>
              ))}
            </div>
          ))}
        </div>
        <div style={{ borderTop: '1px solid #374151', paddingTop: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#fff', marginBottom: 4 }}>Cascady</div>
            <div style={{ fontSize: 13, color: '#9CA3AF' }}>Visual AI image generation canvas</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <a href="#" className="hf-social-icon" aria-label="Twitter" style={{ color: '#9CA3AF', transition: 'color 0.2s' }}>
              <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24"><path d="M23.643 4.937c-.835.37-1.732.62-2.675.733.962-.576 1.7-1.49 2.048-2.578-.9.534-1.897.922-2.958 1.13-.85-.904-2.06-1.47-3.4-1.47-2.572 0-4.658 2.086-4.658 4.66 0 .364.042.718.12 1.06-3.873-.195-7.304-2.05-9.602-4.868-.4.69-.63 1.49-.63 2.342 0 1.616.823 3.043 2.072 3.878-.764-.025-1.482-.234-2.11-.583v.06c0 2.257 1.605 4.14 3.737 4.568-.392.106-.803.162-1.227.162-.3 0-.593-.028-.877-.082.593 1.85 2.313 3.198 4.352 3.234-1.595 1.25-3.604 1.995-5.786 1.995-.376 0-.747-.022-1.112-.065 2.062 1.323 4.51 2.093 7.14 2.093 8.57 0 13.255-7.098 13.255-13.254 0-.2-.005-.402-.014-.602.91-.658 1.7-1.477 2.323-2.41z"/></svg>
            </a>
            <a href="#" className="hf-social-icon" aria-label="GitHub" style={{ color: '#9CA3AF', transition: 'color 0.2s' }}>
              <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24"><path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/></svg>
            </a>
            <a href="#" className="hf-social-icon" aria-label="LinkedIn" style={{ color: '#9CA3AF', transition: 'color 0.2s' }}>
              <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
            </a>
          </div>
        </div>
        <div style={{ textAlign: 'center', marginTop: 24, fontSize: 13, color: '#6B7280' }}>
          © 2026 Cascady. All rights reserved.
        </div>
      </div>
    </footer>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────
export function HomepageFinal() {
  return (
    <div style={{ minHeight: '100vh', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", color: DARK, overflowX: 'hidden' }}>
      <Nav />
      <Hero />
      <Features />
      <CanvasExperience />
      <HowItWorks />
      <UseCases />
      <Testimonials />
      <PricingPreview />
      <FinalCTA />
      <Footer />
      <style>{`
        html { scroll-behavior: smooth; }

        @keyframes hfFloat {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-20px) scale(1.05); }
        }
        .hf-float-circle { animation: hfFloat 8s ease-in-out infinite; }
        .hf-float-delay { animation-delay: 2s; }
        .hf-float-delay2 { animation-delay: 4s; }

        .hf-btn-primary:hover { transform: translateY(-2px); filter: brightness(1.08); box-shadow: 0 8px 28px ${GREEN_20} !important; }
        .hf-btn-secondary:hover { border-color: ${GREEN} !important; color: ${GREEN} !important; }
        .hf-feature-card:hover { transform: translateY(-4px); border-color: ${GREEN} !important; box-shadow: 0 8px 24px ${GREEN_20} !important; }
        .hf-footer-link:hover { color: ${GREEN} !important; }
        .hf-social-icon:hover { color: ${GREEN} !important; }

        @media (max-width: 1024px) {
          .hf-features-grid, .hf-steps-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .hf-footer-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .hf-step-arrow { display: none !important; }
        }
        @media (max-width: 768px) {
          .hf-nav-links { display: none !important; }
          .hf-hamburger { display: block !important; }
          .hf-features-grid, .hf-steps-grid, .hf-usecases-grid, .hf-testimonials-grid { grid-template-columns: 1fr !important; }
          .hf-footer-grid { grid-template-columns: 1fr !important; }
          .hf-hero-ctas { flex-direction: column !important; align-items: stretch !important; }
          .hf-hero-ctas a { text-align: center; }
          .hf-canvas-nodes, .hf-canvas-full { flex-direction: column !important; gap: 12px !important; }
          .hf-arrow { transform: rotate(90deg); }
          .hf-stats-bar { flex-direction: column !important; gap: 20px !important; }
          .hf-canvas-annotations { justify-content: center !important; }
        }
      `}</style>
    </div>
  );
}
